import { useState, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import type { Chapter } from '../../services/types';

interface ChapterContentEditorModalProps {
  novelId: string;
  chapter: Chapter;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedChapter: { chapterId: string; index: number; title: string; wordCount: number }) => void;
}

export function ChapterContentEditorModal({
  novelId,
  chapter,
  isOpen,
  onClose,
  onSuccess
}: ChapterContentEditorModalProps) {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState(chapter.title);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setContent(text);
    } catch {
      setError('讀取檔案失敗');
    }
  }, []);

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError('請輸入章節內容');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await api.uploadChapter(novelId, {
        index: chapter.chapter_index,
        title: title.trim(),
        content: content.trim()
      });
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const wordCount = content.length;

  return (
    <div className="modal">
      <div className="overlay" onClick={onClose} />
      <div className="modal-content max-w-2xl">
        <div className="card-header flex items-center justify-between">
          <h3 className="heading-section">編輯章節內容</h3>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
            disabled={loading}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="card-body space-y-4">
          {/* 章節資訊 */}
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="body-small">第 {chapter.chapter_index} 章</span>
              <span className="body-small">
                目前字數: <span className={chapter.word_count === 0 ? 'text-danger-500' : ''}>{chapter.word_count.toLocaleString()}</span>
              </span>
            </div>
          </div>

          {/* 章節標題 */}
          <div>
            <label className="block body-small mb-1">章節標題</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input w-full"
              placeholder="輸入章節標題"
            />
          </div>

          {/* 內容輸入區 */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="body-small">章節內容</label>
              <span className="body-small">
                新內容字數: {wordCount.toLocaleString()}
              </span>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="textarea w-full h-64 font-mono text-sm"
              placeholder="貼上或輸入章節內容..."
            />
          </div>

          {/* 檔案上傳 */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-outline btn-sm"
              disabled={loading}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              選擇 .txt 檔案
            </button>
            <span className="body-small ml-2">或直接在上方貼上內容</span>
          </div>

          {/* 提示 */}
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
            <p className="body-small text-primary-700 dark:text-primary-300">
              <strong>提示：</strong>系統會自動將簡體中文轉換為繁體中文。
            </p>
          </div>

          {/* 錯誤訊息 */}
          {error && (
            <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-3">
              <p className="body-small text-danger-700 dark:text-danger-300">{error}</p>
            </div>
          )}
        </div>

        <div className="card-footer flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-md"
            disabled={loading}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-md"
            disabled={loading || !content.trim()}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                儲存中...
              </span>
            ) : (
              '儲存'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
